// ─── src/chat/AuraMiniBar.jsx ─────────────────────────────────────────────────
// Desktop: centered floating modal (680px wide).
// Mobile:  bottom sheet, slides up from screen bottom, full-width,
//          rounded top corners, dismissible by tapping the backdrop.
import { useState, useRef, useEffect, useCallback } from "react";
import { useAura } from "../stores/auraCore";
import { useFloatingUi } from "../components/FloatingUiManager";
import { track } from "../utils/track";
import Icon from "./Icons";
import AuraChatImageStrip from "./AuraChatImageStrip";

const KEYFRAMES = `
  @keyframes aura-float-in {
    0%   { opacity: 0; transform: translate(-50%, calc(-50% + 7px)) scale(0.987); filter: blur(9px); }
    28%  { opacity: 0.55; filter: blur(2.5px); }
    68%  { opacity: 1; filter: blur(0px); transform: translate(-50%, calc(-50% + 1.2px)) scale(0.9994); }
    100% { opacity: 1; transform: translate(-50%, -50%) scale(1); filter: blur(0px); }
  }
  @keyframes aura-slide-up {
    0%   { opacity: 0; transform: translateY(100%); }
    60%  { opacity: 1; }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes aura-fade-bg {
    0%   { opacity: 0; }
    100% { opacity: 1; }
  }
`;

export default function AuraMiniBar() {
  const messages      = useAura((s) => s.messages);
  const isTyping      = useAura((s) => s.isTyping);
  // Both null whenever streaming isn't active — the dots below render unchanged then.
  const streamStage    = useAura((s) => s.streamStage);
  const streamingText  = useAura((s) => s.streamingText);
  const sendMessage   = useAura((s) => s.sendMessage);
  const openWorkspace = useAura((s) => s.openWorkspace);
  const closeChat     = useAura((s) => s.closeChat);
  // nudgeDiscovery is a discovery-page concern; AuraMiniBar doesn't need it
  const { isMobile: mobile } = useFloatingUi();

  const [input,     setInput]     = useState("");
  const [listening, setListening] = useState(false);
  const threadRef = useRef(null);
  const inputRef  = useRef(null);

  // Last 4 messages for the compact thread
  const thread = messages.slice(-4);

  // Auto-scroll thread
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, isTyping]);

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // ESC → close
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") closeChat(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [closeChat]);

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    track('aura_query', { query: input.trim(), char_count: input.trim().length });
    sendMessage(input);
    setInput("");
  }, [input, sendMessage]);

  const handleKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const handleVoice = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || listening) return;
    const rec = new SR();
    rec.lang = "en-GB";
    rec.interimResults = false;
    rec.onstart  = () => setListening(true);
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput((prev) => (prev ? prev + " " + t : t));
    };
    rec.onend   = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
  }, [listening]);

  const hasSR = typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  /* ── Shared inner content ──────────────────────────────────────────────────── */
  const innerContent = (
    <>
      {/* ── Header ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px 13px",
        borderBottom: "1px solid rgba(211,154,33,0.12)",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(211,154,33,0.12)", border: "1px solid rgba(211,154,33,0.32)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, color: "#d39a21", flexShrink: 0,
          }}>✦</div>
          <div>
            <div style={{
              fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13,
              color: "#f5f0e8", letterSpacing: "0.3px", lineHeight: 1, marginBottom: 4,
            }}>Chat with Aura</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
              <span style={{ fontFamily: "var(--font-body)", fontSize: 10, color: "rgba(245,240,232,0.4)", letterSpacing: "0.5px" }}>
                Online
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!mobile && (
            <button
              onClick={openWorkspace}
              style={{
                fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600,
                color: "#d39a21", background: "rgba(211,154,33,0.1)",
                border: "1px solid rgba(211,154,33,0.25)", borderRadius: 6,
                padding: "5px 12px", cursor: "pointer", letterSpacing: "0.4px",
                whiteSpace: "nowrap", transition: "background 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(211,154,33,0.2)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(211,154,33,0.1)"}
            >Full chat ↗</button>
          )}
          <button
            onClick={closeChat}
            aria-label="Close chat"
            style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "rgba(255,255,255,0.06)", border: "none",
              color: "rgba(245,240,232,0.45)", cursor: "pointer", fontSize: 17,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#f5f0e8"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(245,240,232,0.45)"; }}
          >×</button>
        </div>
      </div>

      {/* ── Compact thread ── */}
      <div
        ref={threadRef}
        style={{
          flex: mobile ? "1 1 auto" : "none",
          height: mobile ? undefined : 140,
          minHeight: mobile ? 80 : undefined,
          maxHeight: mobile ? "40vh" : undefined,
          overflowY: "auto",
          padding: "12px 18px",
          display: "flex", flexDirection: "column", gap: 7,
          background: "#FFFDF9",
        }}
      >
        {thread.map((m) => (
          <div
            key={m.id}
            style={{
              display: "flex", flexDirection: "column",
              alignItems: m.from === "user" ? "flex-end" : "flex-start",
              gap: 4,
            }}
          >
            <div style={{
              maxWidth: "82%", padding: "7px 12px",
              borderRadius: m.from === "user" ? "10px 10px 2px 10px" : "10px 10px 10px 2px",
              background: m.from === "user" ? "#d39a21" : "#F0ECE4",
              fontFamily: "var(--font-body)", fontSize: 12.5, lineHeight: 1.5,
              color: m.from === "user" ? "#0f0d0a" : "#3a3530",
            }}>{m.text}</div>
            {m.from === "aura" && m.images?.length > 0 && (
              <div style={{ maxWidth: "90%", width: "100%" }}>
                <AuraChatImageStrip images={m.images} />
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          streamingText ? (
            <div style={{ padding: "4px 2px", fontSize: 13, lineHeight: 1.5, color: "#3a3530" }}>
              {streamingText}
              <span style={{ display: "inline-block", width: 5, height: 11, marginLeft: 2, background: "rgba(211,154,33,0.7)", verticalAlign: "text-bottom", animation: "lwd-cursor-blink 0.8s steps(1) infinite" }} />
            </div>
          ) : streamStage ? (
            <div style={{ display: "flex", gap: 7, alignItems: "center", padding: "4px 2px" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(211,154,33,0.5)", display: "inline-block", animation: "lwd-dot-pulse 1.4s ease infinite" }} />
              <span style={{ fontSize: 12, color: "rgba(58,53,48,0.65)" }}>{streamStage.label}</span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 5, padding: "4px 2px" }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: "rgba(211,154,33,0.5)", display: "inline-block",
                  animation: `lwd-dot-pulse 1.4s ease ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          )
        )}
      </div>

      {/* ── Input row ── */}
      <div style={{
        padding: mobile ? "10px 14px calc(10px + env(safe-area-inset-bottom, 0px))" : "10px 14px 16px",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        background: "#FFFDF9",
        flexShrink: 0,
      }}>
        {mobile && (
          <button
            onClick={openWorkspace}
            style={{
              width: "100%", marginBottom: 8,
              fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600,
              color: "#d39a21", background: "rgba(211,154,33,0.08)",
              border: "1px solid rgba(211,154,33,0.22)", borderRadius: 8,
              padding: "8px 12px", cursor: "pointer", letterSpacing: "0.4px",
              transition: "background 0.2s",
            }}
          >Open full chat ↗</button>
        )}
        <div style={{
          display: "flex", gap: 6, alignItems: "center",
          background: "#F5F2ED", border: "1px solid rgba(211,154,33,0.15)",
          borderRadius: 12, padding: "6px 8px",
        }}>
          {hasSR && (
            <button
              onClick={handleVoice}
              aria-label={listening ? "Listening…" : "Voice input"}
              style={{
                width: 32, height: 32, flexShrink: 0, borderRadius: 8,
                background: listening ? "rgba(211,154,33,0.15)" : "rgba(0,0,0,0.04)",
                border: "none", color: listening ? "#d39a21" : "#8a857e",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
                animation: listening ? "lwd-dot-pulse 1.4s ease infinite" : "none",
              }}
              onMouseEnter={(e) => { if (!listening) e.currentTarget.style.color = "#d39a21"; }}
              onMouseLeave={(e) => { if (!listening) e.currentTarget.style.color = "#8a857e"; }}
            ><Icon name="mic" size={15} /></button>
          )}
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Guest count, style, budget…"
            style={{
              flex: 1, background: "none", border: "none",
              padding: "6px 4px", fontFamily: "var(--font-body)",
              fontSize: 13, color: "#1a1816", outline: "none", caretColor: "#d39a21",
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            aria-label="Send message"
            style={{
              width: 32, height: 32, flexShrink: 0, borderRadius: 8,
              background: input.trim() ? "#d39a21" : "rgba(211,154,33,0.12)",
              border: "none", color: input.trim() ? "#0f0d0a" : "rgba(211,154,33,0.35)",
              cursor: input.trim() ? "pointer" : "default",
              fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
            }}
          >↑</button>
        </div>
      </div>
    </>
  );

  /* ── Backdrop (shared) ─────────────────────────────────────────────────────── */
  const backdrop = (
    <div
      onClick={closeChat}
      style={{
        position: "fixed", inset: 0, zIndex: 1001,
        background: "rgba(10,9,7,0.22)",
        backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
        animation: "aura-fade-bg 0.65s cubic-bezier(0.4,0,0.2,1) both",
      }}
    />
  );

  return (
    <>
      <style>{KEYFRAMES}</style>
      {backdrop}

      {mobile ? (
        /* ── Mobile: bottom sheet ─────────────────────────────────────────────── */
        <div
          role="dialog"
          aria-label="Aura mini chat"
          style={{
            position: "fixed",
            bottom: 0, left: 0, right: 0,
            zIndex: 1002,
            borderRadius: "20px 20px 0 0",
            overflow: "hidden",
            background: "#1E1C19",
            border: "1px solid rgba(211,154,33,0.22)",
            borderBottom: "none",
            boxShadow: "0 -16px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(211,154,33,0.07)",
            animation: "aura-slide-up 0.38s cubic-bezier(0.25,0.46,0.45,0.94) both",
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          }}
        >
          {/* Drag handle */}
          <div style={{
            display: "flex", justifyContent: "center",
            padding: "10px 0 4px",
            flexShrink: 0,
          }}>
            <div style={{
              width: 36, height: 4, borderRadius: 2,
              background: "rgba(255,255,255,0.15)",
            }} />
          </div>
          {innerContent}
        </div>
      ) : (
        /* ── Desktop: centered modal ───────────────────────────────────────────── */
        <div
          role="dialog"
          aria-label="Aura mini chat"
          style={{
            position: "fixed",
            top: "50%", left: "50%",
            zIndex: 1002,
            width: 680, maxWidth: "calc(100vw - 32px)",
            borderRadius: 20, overflow: "hidden",
            background: "#1E1C19",
            border: "1px solid rgba(211,154,33,0.22)",
            boxShadow: "0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(211,154,33,0.07), 0 0 40px rgba(211,154,33,0.05)",
            animation: "aura-float-in 0.92s cubic-bezier(0.4,0,0.06,1) both",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {innerContent}
        </div>
      )}
    </>
  );
}
